import { Navigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { MainLoader } from '@socialgoodsoftware/components';
import { userApi } from '../../user-api';
import { enrollMfaPath } from '../../lib/api-client';
import { featureQueryKey } from '../../lib/api-client';
import { isOrganizationDeactivatedError } from '../../lib/api-client';
import { buildLoginRedirect } from '../../lib/login-redirect';

import type { ReactElement } from 'react';
import type { ReactNode } from 'react';

type GuardMode = 'protected' | 'guest';

export function RequireAuth({ mode = 'protected', children }: { mode?: GuardMode; children: ReactNode }): ReactElement {
  const location = useLocation();

  // Guest pages (login/signup): zero network. Server redirects if a session already
  // exists. First client network call is the credential submit on the form.
  // Protected pages: one shared getUser probe (same queryKey as OrganizationProvider).
  const userQuery = userApi.getUser.useQuery({
    queryKey: featureQueryKey('user'),
    enabled: mode === 'protected',
    retry: false,
  });

  // Only block on the initial probe (no settled data yet). Background refetches
  // (session poll seed, org switch invalidate, window focus) must not remount the
  // shell — that flashes "Checking your session" after "Loading account".
  // Post-login completeAuthenticatedLanding uses removeQueries so isPending is true again.
  let sessionProbePending = false;
  if (mode === 'protected') {
    if (userQuery.isPending) {
      sessionProbePending = true;
    } else if (userQuery.isFetching && userQuery.data === undefined) {
      sessionProbePending = true;
    }
  }

  let element: ReactElement;
  if (mode === 'guest') {
    element = <>{children}</>;
  } else if (sessionProbePending) {
    element = <MainLoader loaderText="Loading account" />;
  } else if (userQuery.isSuccess && userQuery.data.status === 200) {
    if (userQuery.data.body.mfaEnrollmentRequired === true) {
      const continueTarget = `${location.pathname}${location.search}`;
      element = <Navigate to={`${enrollMfaPath}?continue=${encodeURIComponent(continueTarget)}`} replace />;
    } else {
      element = <>{children}</>;
    }
  } else if (isOrganizationDeactivatedError(userQuery.error ?? userQuery.data)) {
    element = <MainLoader loaderText="Loading account" />;
  } else {
    element = <Navigate to={buildLoginRedirect(location.pathname, location.search)} replace />;
  }

  return element;
}
